import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PlaylistFilters } from '@/lib/playlist-utils';

const STORAGE_KEY = 'beatbattle_personal_playlists_v1';

export interface PersonalPlaylist {
  id: string;
  name: string;
  description?: string;
  filters: PlaylistFilters;
  trackIds: string[];
  createdAt: string;
  updatedAt: string;
}

interface CreatePlaylistPayload {
  name: string;
  description?: string;
  filters: PlaylistFilters;
  trackIds: string[];
}

interface UpdatePlaylistPayload {
  name?: string;
  description?: string;
  trackIds?: string[];
  filters?: PlaylistFilters;
}

function loadPlaylists(): PersonalPlaylist[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as PersonalPlaylist[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch (error) {
    console.warn('Failed to load personal playlists', error);
    return [];
  }
}

function persistPlaylists(playlists: PersonalPlaylist[]) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(playlists));
  } catch (error) {
    console.warn('Failed to persist personal playlists', error);
  }
}

export function usePersonalPlaylists() {
  const [playlists, setPlaylists] = useState<PersonalPlaylist[]>(() => loadPlaylists());

  useEffect(() => {
    persistPlaylists(playlists);
  }, [playlists]);

  const createPlaylist = useCallback((payload: CreatePlaylistPayload) => {
    const now = new Date().toISOString();
    const newPlaylist: PersonalPlaylist = {
      id: `personal_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: payload.name.trim() || 'Untitled Playlist',
      description: payload.description?.trim() || undefined,
      filters: payload.filters,
      trackIds: Array.from(new Set(payload.trackIds)),
      createdAt: now,
      updatedAt: now,
    };
    setPlaylists((prev) => [newPlaylist, ...prev]);
    return newPlaylist;
  }, []);

  const updatePlaylist = useCallback((id: string, updates: UpdatePlaylistPayload) => {
    setPlaylists((prev) =>
      prev.map((playlist) => {
        if (playlist.id !== id) {
          return playlist;
        }
        const updated: PersonalPlaylist = {
          ...playlist,
          ...updates,
          filters: updates.filters ?? playlist.filters,
          trackIds: updates.trackIds ? Array.from(new Set(updates.trackIds)) : playlist.trackIds,
          name: updates.name?.trim() || playlist.name,
          description: updates.description?.trim() || playlist.description,
          updatedAt: new Date().toISOString(),
        };
        return updated;
      }),
    );
  }, []);

  const deletePlaylist = useCallback((id: string) => {
    setPlaylists((prev) => prev.filter((playlist) => playlist.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setPlaylists([]);
  }, []);

  const totalPlaylists = useMemo(() => playlists.length, [playlists]);

  return {
    playlists,
    totalPlaylists,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    clearAll,
  };
}


